import { isDefined } from "@clipboard-health/util-ts";
import { formatDateRangeDuration } from "@src/appV2/redesign/utils/formatDateRangeDuration";
import { differenceInMilliseconds, isBefore, isSameSecond } from "date-fns";

import { getOvertimeInfo } from "./getOvertimeInfo";
import { getShiftProgressAndWorkedTime } from "./getShiftProgressAndWorkedTime";

interface ComputePendingProgressParams {
  clockInDate: Date;
  adjustedClockInDate: Date;
  shiftEndDate: Date;
}

function computePendingProgress(params: ComputePendingProgressParams): number {
  const { clockInDate, adjustedClockInDate, shiftEndDate } = params;
  const totalDurationInMs = differenceInMilliseconds(shiftEndDate, adjustedClockInDate);
  const pendingDurationInMs = differenceInMilliseconds(clockInDate, adjustedClockInDate);
  return Math.min(Math.max(pendingDurationInMs / totalDurationInMs, 0), 1);
}

export function getShiftProgress(params: {
  clockInDate: Date;
  adjustedClockInDate?: Date;
  shiftStartDate: Date;
  shiftEndDate: Date;
  now: Date;
}) {
  const { clockInDate, adjustedClockInDate, shiftStartDate, shiftEndDate, now } = params;

  const hasPendingAdjustment =
    isDefined(adjustedClockInDate) && isBefore(adjustedClockInDate, clockInDate);

  const pendingProgress = hasPendingAdjustment
    ? computePendingProgress({ clockInDate, adjustedClockInDate, shiftEndDate })
    : undefined;

  const isShiftInProgress = isBefore(now, shiftEndDate) || isSameSecond(now, shiftEndDate);

  if (isShiftInProgress) {
    // When an adjustment is pending, use the adjusted clock-in as the arc base so the full
    // claimed time is represented on the outer circle.
    const { progress, timeWorked } = getShiftProgressAndWorkedTime({
      clockInDate: hasPendingAdjustment ? adjustedClockInDate : clockInDate,
      shiftStartDate,
      shiftEndDate,
      now,
    });

    return {
      progress,
      secondaryProgress: 0,
      timeWorked,
      timeLeft: `${formatDateRangeDuration({
        startDate: now,
        endDate: shiftEndDate,
        format: "abbreviated",
        alwaysDisplayHours: false,
      })} left`,
      isOvertime: false,
      overtimeText: "",
      totalTimeWorked: "",
      pendingProgress,
    };
  }

  // During overtime, overtime progress itself is calculated from the confirmed clock-in time.
  // The pending clock-in adjustment (if any) surfaces on the inner (secondary) circle.
  const { progress, secondaryProgress, overtimeText, totalTimeWorked } = getOvertimeInfo({
    clockInDate,
    shiftEndDate,
    now,
  });

  return {
    progress,
    secondaryProgress,
    timeWorked: "0s",
    timeLeft: "0h 00m left",
    isOvertime: true,
    overtimeText,
    totalTimeWorked,
    pendingProgress,
  };
}
